import ky from "ky";
import type { MonthDayAvailability, SEMItemSlots } from "./types/slots";
import type { Flow } from "./types/flow";
import type { Image } from "./types/image";
import type { SemItemPricing } from "./types/pricing";
import type {
  ReservationSession,
  ReservationSessionStatus,
} from "./types/session";

export interface OrderConfirmation {
  status: ReservationSessionStatus;
  personCount: number;
  selectedDate: string;
  reservationTitle: string | null;
  schedule: {
    name: string;
    startTime: string;
    endTime: string;
    show: boolean;
  }[];
  thankYouText: string | null;
  thankYouImage: Image | null;
  showAddToCalendar: boolean;
}

export const apiClient = ky.create({
  prefixUrl: import.meta.env.VITE_API_URL,
});

export const getFlow = async () => {
  return await apiClient.get("v1/client/flow").json<Flow>();
};

export const getMonthAvailability = async ({
  flowStepId,
  month,
  year,
  persons,
}: {
  flowStepId: string;
  month: number;
  year: number;
  persons: number;
}) => {
  return await apiClient
    .get(`v1/client/flow-step/${flowStepId}/month-availability`, {
      searchParams: {
        month,
        year,
        persons,
      },
    })
    .json<MonthDayAvailability[]>();
};

export const getDateAvailability = async ({
  flowStepId,
  date,
  persons,
}: {
  flowStepId: string;
  date: string;
  persons: number;
}) => {
  return await apiClient
    .get(`v1/client/flow-step/${flowStepId}/availability`, {
      searchParams: {
        date,
        persons,
      },
    })
    .json<SEMItemSlots[]>();
};

export const getSemItemPricing = async (semItemId: string) => {
  return await apiClient
    .get(`v1/client/sem-item/${semItemId}/pricing`)
    .json<SemItemPricing>();
};

export const createSession = async (params: {
  flowConfigId: string;
  semItemConfigId: string;
  date: string;
  slotTime: string;
  persons: number;
  priceProductId: number;
}) => {
  return await apiClient
    .post("v1/client/session", { json: params })
    .json<ReservationSession>();
};

export const getSession = async (sessionId: string) => {
  return await apiClient
    .get(`v1/client/session/${sessionId}`)
    .json<ReservationSession>();
};

export const upsertSessionProduct = async (
  sessionId: string,
  params: { semProductConfigId: string; quantity: number },
) => {
  return await apiClient
    .post(`v1/client/session/${sessionId}/product`, { json: params })
    .json<ReservationSession>();
};

export const cancelSession = async (sessionId: string) => {
  return await apiClient
    .delete(`v1/client/session/${sessionId}`)
    .json<{ ok: boolean }>();
};

export const getConfirmation = async (sessionId: string) => {
  return await apiClient
    .get(`v1/checkout/sem/confirmation/${sessionId}`)
    .json<OrderConfirmation>();
};

export const applySessionDiscount = async (
  sessionId: string,
  discountCodeId: string | null,
  appliedDiscountCode?: string | null,
) => {
  return await apiClient
    .patch(`v1/client/session/${sessionId}/discount`, {
      json: { discountCodeId, appliedDiscountCode: appliedDiscountCode ?? null },
    })
    .json<ReservationSession>();
};

export const validateDiscount = async (params: {
  code: string;
  sessionId: string;
}) => {
  return await apiClient
    .post("v1/discount/validate", { json: params })
    .json<{
      valid: boolean;
      message?: string;
      discountCodeId?: string;
      discountType?: "percentage" | "fixed";
      discountValue?: string;
      name?: string;
      appliedCode?: string;
    }>();
};

export const checkout = async (params: {
  sessionId: string;
  gender: "Male" | "Female" | "Unknown";
  firstName: string;
  lastName: string;
  company?: string;
  email: string;
  phone: string;
  note?: string;
  discountCodeId?: string;
}) => {
  return await apiClient
    .post("v1/checkout/sem", { json: params, timeout: 60_000 })
    .json<{ sessionId: string; paymentUrl?: string; paid?: boolean }>();
};
